import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

export type OverflowToolbarItem = {
    /** Stable, unique identity — also used as the React key for the rendered slot. */
    readonly id: string;
    /** Full inline rendering, shown while there is room for it. */
    readonly children: ReactNode;
    /**
     * Compact inline rendering. When provided, this item's reduction keeps it in
     * the bar but swaps to this smaller form. Prefer over `overflow` when set.
     */
    readonly collapsed?: ReactNode;
    /**
     * Overflow-menu rendering. When provided, this item's reduction removes it
     * from the bar and hands this node to `renderTrailing`.
     */
    readonly overflow?: ReactNode;
    /**
     * Reduction order. Under space pressure, items are reduced one at a time in
     * ascending `priority` (lowest first). Items without `collapsed`/`overflow`
     * never reduce and always stay inline.
     */
    readonly priority?: number;
};

export type OverflowToolbarOverflowItem = {
    readonly id: string;
    readonly node: ReactNode;
};

export type OverflowToolbarProps = {
    readonly className?: string;
    /**
     * Stretch the row to the toolbar's width, so an item with `ml-auto` pushes the
     * items after it to the end while the rest stay at the start.
     */
    readonly fill?: boolean;
    readonly items: readonly OverflowToolbarItem[];
    /**
     * Always-present trailing slot (e.g. a "..." menu). Receives the items that
     * are currently pushed to overflow, in reduction order, so the caller can
     * render them inside its own menu.
     */
    readonly renderTrailing: (overflow: readonly OverflowToolbarOverflowItem[]) => ReactNode;
};

type ToolbarLayout = {
    readonly inline: readonly { readonly id: string; readonly node: ReactNode }[];
    readonly overflow: readonly OverflowToolbarOverflowItem[];
};

/** Hysteresis, in px, before a reduced item is restored — avoids flicker at the boundary. */
const GROW_HYSTERESIS = 4;

const isReducible = (item: OverflowToolbarItem): boolean => item.collapsed !== undefined || item.overflow !== undefined;

export const deriveToolbarLayout = (items: readonly OverflowToolbarItem[], level: number): ToolbarLayout => {
    // Track reduction by position, not id, so callers with duplicate ids can't collapse two
    // items into one membership test / overwrite each other in the overflow set.
    const reducedIndices = items
        .map((item, index) => ({ index, item }))
        .filter((entry) => isReducible(entry.item))
        .sort((a, b) => (a.item.priority ?? 0) - (b.item.priority ?? 0) || a.index - b.index)
        .slice(0, level)
        .map((entry) => entry.index);
    const reducedIndexSet = new Set(reducedIndices);

    const inline: { id: string; node: ReactNode }[] = [];
    const overflowByIndex = new Map<number, OverflowToolbarOverflowItem>();

    items.forEach((item, index) => {
        if (!reducedIndexSet.has(index)) {
            inline.push({ id: item.id, node: item.children });
            return;
        }

        if (item.collapsed !== undefined) {
            inline.push({ id: item.id, node: item.collapsed });
        } else {
            overflowByIndex.set(index, { id: item.id, node: item.overflow });
        }
    });

    const overflow = reducedIndices
        .map((index) => overflowByIndex.get(index))
        .filter((entry): entry is OverflowToolbarOverflowItem => entry !== undefined);

    return { inline, overflow };
};

export const OverflowToolbar: FunctionComponent<OverflowToolbarProps> = ({
    className,
    fill = false,
    items,
    renderTrailing,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    // boundaries[L] = content width the toolbar needed at level L-1 (the width
    // that forced entering level L). Restore level L-1 once that width fits again.
    const boundariesRef = useRef<number[]>([]);
    const [level, setLevel] = useState(0);

    const maxLevel = useMemo(() => items.filter(isReducible).length, [items]);
    const layout = useMemo(() => deriveToolbarLayout(items, level), [items, level]);
    // Identity of the reducible set; when it changes the stored per-level pixel boundaries no
    // longer describe the current items, so reset and let the measure loop reconverge.
    const reducibleSignature = items
        .filter(isReducible)
        .map((item) => item.id)
        .join('|');

    const measure = useCallback((): void => {
        const container = containerRef.current;
        const row = rowRef.current;

        if (container === null || row === null) {
            return;
        }

        const available = container.clientWidth;
        const required = row.scrollWidth;

        setLevel((current) => {
            if (required > available + 1 && current < maxLevel) {
                boundariesRef.current[current + 1] = required;

                return current + 1;
            }

            const restoreWidth = boundariesRef.current[current] ?? Number.POSITIVE_INFINITY;
            if (current > 0 && available > restoreWidth + GROW_HYSTERESIS) {
                return current - 1;
            }

            return current;
        });
    }, [maxLevel]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: reducibleSignature is the trigger — reset the measurement state whenever the reducible set changes.
    useLayoutEffect(() => {
        boundariesRef.current = [];
        setLevel(0);
    }, [reducibleSignature]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure after each layout change so multi-step reductions converge without a container resize.
    useLayoutEffect(() => {
        const frameId = window.requestAnimationFrame(measure);

        return () => window.cancelAnimationFrame(frameId);
    }, [measure, layout]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const row = rowRef.current;

        if (container === null || row === null || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => measure());
        observer.observe(container);
        observer.observe(row);

        return () => observer.disconnect();
    }, [measure]);

    return (
        <div
            ref={containerRef}
            className={clsx(
                'relative flex min-w-0 flex-1 basis-0 items-center justify-end contain-inline-size',
                'overflow-x-clip overflow-y-visible [overflow-clip-margin:0.75rem]',
                className,
            )}
        >
            {/* `pr-m` keeps the trailing control's shadow inside the clipped container's edge. */}
            <div ref={rowRef} className={clsx('flex w-max shrink-0 items-center gap-s pr-m', fill && 'min-w-full')}>
                {layout.inline.map((entry) => (
                    <Fragment key={entry.id}>{entry.node}</Fragment>
                ))}
                {renderTrailing(layout.overflow)}
            </div>
        </div>
    );
};
